/**
 * PropertyRequestTable Component
 * Table displaying property requests
 */

import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { PropertyRequest } from "../../../store/api/propertyRequestsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface PropertyRequestTableProps {
	requests: PropertyRequest[];
	isLoading: boolean;
	onRequestClick: (request: PropertyRequest) => void;
	onAccept: (request: PropertyRequest) => void;
	onDecline: (request: PropertyRequest) => void;
}

const PropertyRequestTable: React.FC<PropertyRequestTableProps> = ({
	requests,
	isLoading,
	onRequestClick,
	onAccept,
	onDecline,
}) => {
	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	// Using centralized formatDateTime utility

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<string, { color: string; label: string }> = {
			pending: { color: "bg-warning/20 text-warning border border-warning/30", label: "Pending" },
			under_review: {
				color: "bg-primary/20 text-primary border border-primary/30",
				label: "Under Review",
			},
			accepted: { color: "bg-success/20 text-success border border-success/30", label: "Accepted" },
			declined: { color: "bg-error/20 text-error border border-error/30", label: "Declined" },
			expired: { color: "bg-muted/30 text-foreground border border-border", label: "Expired" },
		};

		const config = statusConfig[status] || statusConfig.pending;
		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	const getRequestTypeBadge = (type: string) => {
		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium border ${
					type === "lease"
						? "bg-primary/20 text-primary border-primary/30 dark:bg-primary/30 dark:text-primary dark:border-primary/40"
						: "bg-accent/20 text-accent border-accent/30 dark:bg-accent/30 dark:text-accent dark:border-accent/40"
				}`}
			>
				{type === "lease" ? "Lease" : "Buy"}
			</span>
		);
	};

	if (isLoading) {
		return (
			<div className="bg-card border border-border rounded-lg p-12">
				<div className="flex items-center justify-center">
					<Icon
						name="Loader2"
						size={32}
						className="animate-spin text-primary"
					/>
				</div>
			</div>
		);
	}

	if (requests.length === 0) {
		return (
			<div className="bg-card border border-border rounded-lg p-12 text-center">
				<Icon
					name="Inbox"
					size={48}
					className="mx-auto mb-4 text-muted-foreground opacity-50"
				/>
				<h3 className="text-lg font-semibold text-foreground mb-2">
					No Requests Found
				</h3>
				<p className="text-muted-foreground">
					No property requests match your current filters.
				</p>
			</div>
		);
	}

	return (
		<div className="bg-card border border-border rounded-lg overflow-hidden">
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
						<thead className="bg-muted/50 border-b border-border">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Requester
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Property
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Type
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Amount
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Status
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
									Date
								</th>
								<th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
									Actions
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-border">
							{requests.map((request) => (
								<tr
									key={request.id}
									className="hover:bg-muted/30 smooth-transition cursor-pointer"
									onClick={() => onRequestClick(request)}
								>
									<td className="px-4 py-4">
										<div>
											<p className="text-sm font-medium text-foreground">
												{request.first_name} {request.last_name}
											</p>
											<p className="text-xs text-muted-foreground">
												{request.email}
											</p>
										</div>
									</td>
									<td className="px-4 py-4">
										<p className="text-sm text-foreground truncate max-w-xs">
											{request.property_name || "N/A"}
										</p>
									</td>
									<td className="px-4 py-4">
										{getRequestTypeBadge(request.request_type)}
									</td>
									<td className="px-4 py-4">
										<div>
											<p className="text-sm font-medium text-foreground">
												{formatCurrency(request.quoted_amount)}
											</p>
											{request.offer_amount && (
												<p className="text-xs text-muted-foreground">
													Offer: {formatCurrency(request.offer_amount)}
												</p>
											)}
										</div>
									</td>
									<td className="px-4 py-4">
										{getStatusBadge(request.status)}
									</td>
									<td className="px-4 py-4">
										<p className="text-sm text-muted-foreground">
											{formatDateTime(request.created_at)}
										</p>
									</td>
									<td className="px-4 py-4">
										<div
											className="flex items-center justify-end space-x-2"
											onClick={(e) => e.stopPropagation()}
										>
											{request.status === "pending" && (
												<>
													<Button
														variant="outline"
														size="sm"
														onClick={() => onAccept(request)}
													>
														<Icon name="Check" size={16} className="mr-1" />
														Accept
													</Button>
													<Button
														variant="outline"
														size="sm"
														onClick={() => onDecline(request)}
													>
														<Icon name="X" size={16} className="mr-1" />
														Decline
													</Button>
												</>
											)}
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onRequestClick(request)}
												title="View Details"
											>
												<Icon name="Eye" size={16} />
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default PropertyRequestTable;
